/**
 * Search Form Module
 *
 * Handles search form interactions including:
 * - Dynamic form reload when post type changes
 * - Loading states
 * - Advanced search support
 *
 * Replaces legacy jQuery-based search functionality with modern vanilla JavaScript.
 * Note: Geocoding is handled by search-geocode.js on form submit.
 */

/**
 * SearchForm class
 * Manages a single search form instance.
 */
class SearchForm {
	/**
	 * Constructor
	 * @param {HTMLFormElement} formElement - The search form element
	 */
	constructor(formElement) {
		this.form = formElement;
		this.container = formElement.closest('.geodir-search-container');
		this.postTypeSelect = formElement.querySelector('.search_by_post');
		this.nearInput = formElement.querySelector('.snear');
		this.latInput = formElement.querySelector('.sgeo_lat');
		this.lonInput = formElement.querySelector('.sgeo_lon');
		this.submitButton = formElement.querySelector('.geodir_submit_search');
		this.isLoading = false;
		this.nearDefaultValue = this.nearInput ? this.nearInput.value : '';

		this.init();
	}

	/**
	 * Initialize event listeners
	 */
	init() {
		// Listen for post type changes
		if (this.postTypeSelect) {
			this.postTypeSelect.addEventListener('change', (e) => this.handlePostTypeChange(e));
		}

		// Note: Near field geocoding is handled by search-geocode.js on form submit

		// Store keep args from form
		this.keepArgs = this.getKeepArgs();
	}

	/**
	 * Get keep args from form (widget settings)
	 * @returns {Object}
	 */
	getKeepArgs() {
		const keepArgsElement = this.form.querySelector('.geodir-keep-args');
		if (!keepArgsElement) {
			return {};
		}

		const jsonText = keepArgsElement.textContent.trim();
		if (!jsonText || jsonText === '[]' || jsonText === '{}') {
			return {};
		}

		try {
			const parsed = JSON.parse(jsonText);
			// Handle case where it's an array instead of object
			return Array.isArray(parsed) && parsed.length === 0 ? {} : parsed;
		} catch (error) {
			console.warn('GeoDir Search: Failed to parse keep args, using empty object', error);
			return {};
		}
	}

	/**
	 * Handle post type selector change
	 * @param {Event} event
	 */
	async handlePostTypeChange(event) {
		const newPostType = event.target.value;

		if (!newPostType || this.isLoading) {
			return;
		}

		console.log(`GeoDir Search: Loading form for post type: ${newPostType}`);

		// Store current near field value and location type to preserve them
		const currentNearValue = this.nearInput ? this.nearInput.value : '';
		const currentLat = this.latInput ? this.latInput.value : '';
		const currentLon = this.lonInput ? this.lonInput.value : '';
		const locationType = this.form.querySelector('input.geodir-location-search-type');
		const locationTypeValue = locationType ? locationType.value : '';
		const locationTypeName = locationType ? locationType.name : '';

		await this.reloadForm(newPostType, {
			preserveNear: currentNearValue,
			preserveLat: currentLat,
			preserveLon: currentLon,
			preserveLocationType: locationTypeValue,
			preserveLocationTypeName: locationTypeName
		});
	}

	/**
	 * Reload form via API
	 * @param {string} postType - Post type to load
	 * @param {Object} options - Preservation options
	 */
	async reloadForm(postType, options = {}) {
		if (this.isLoading) {
			return;
		}

		this.setLoadingState(true);

		try {
			// Check if this is a "main" form with advanced search
			const isMainForm = this.form.dataset.show === 'main';
			const hasAdvancedForm = isMainForm && document.querySelector('form.geodir-search-show-advanced');

			// Call API
			const response = await window.GeoDir.api.post('search/form', {
				post_type: postType,
				keep_args: this.keepArgs,
				advanced: hasAdvancedForm ? true : false
			});

			if (!response.success || !response.html) {
				throw new Error('Invalid API response');
			}

			// Replace form HTML
			this.container.innerHTML = response.html;

			// If there's an advanced form, update it too
			if (hasAdvancedForm) {
				const newContainer = this.container.querySelector('.geodir-search-has-advanced');
				if (newContainer) {
					const advancedForms = document.querySelectorAll('form.geodir-search-show-advanced');
					advancedForms.forEach(advForm => {
						advForm.innerHTML = newContainer.innerHTML;

						// Check if advanced form has filter categories
						if (!advForm.querySelector('.geodir-filter-cat')) {
							advForm.closest('.geodir-search-container')?.classList.add('d-none');
							advForm.querySelector('.geodir_submit_search')?.closest('.gd-search-field-search')?.style.setProperty('display', 'none');
						} else {
							advForm.closest('.geodir-search-container')?.classList.remove('d-none');
						}
					});

					// Remove the advanced HTML from main container
					newContainer.remove();
				}
			}

			// Restore near field and lat/lon if requested
			if (options.preserveNear) {
				const newNearInput = this.container.querySelector('.snear');
				if (newNearInput) {
					newNearInput.value = options.preserveNear;
				}
			}

			if (options.preserveLat && options.preserveLon) {
				const newLatInput = this.container.querySelector('.sgeo_lat');
				const newLonInput = this.container.querySelector('.sgeo_lon');
				if (newLatInput && newLonInput) {
					newLatInput.value = options.preserveLat;
					newLonInput.value = options.preserveLon;
				}
			}

			// Restore location search type if it existed
			if (options.preserveLocationType && options.preserveLocationTypeName) {
				const newLocationType = this.container.querySelector('input.geodir-location-search-type');
				if (newLocationType) {
					newLocationType.value = options.preserveLocationType;
					newLocationType.name = options.preserveLocationTypeName;
				}
			}

			// Re-initialize this form instance with new DOM elements
			this.form = this.container.querySelector('form[name="geodir-listing-search"]');
			this.postTypeSelect = this.form.querySelector('.search_by_post');
			this.nearInput = this.form.querySelector('.snear');
			this.latInput = this.form.querySelector('.sgeo_lat');
			this.lonInput = this.form.querySelector('.sgeo_lon');
			this.submitButton = this.form.querySelector('.geodir_submit_search');
			this.init();

			// Re-initialize AUI components (hover-swap, etc.)
			if (typeof window.aui_init === 'function') {
				window.aui_init();
			}

			// Trigger custom event for addons
			const event = new CustomEvent('geodir:searchFormLoaded', {
				detail: {
					container: this.container,
					form: this.form,
					postType: postType
				}
			});
			document.body.dispatchEvent(event);

			console.log('GeoDir Search: Form reloaded successfully');

		} catch (error) {
			console.error('GeoDir Search: Form reload failed', error);
			alert('Failed to load search form. Please refresh the page.');
		} finally {
			this.setLoadingState(false);
		}
	}

	/**
	 * Set loading state
	 * @param {boolean} loading
	 */
	setLoadingState(loading) {
		this.isLoading = loading;

		if (!this.submitButton) {
			return;
		}

		if (loading) {
			// Disable button and show spinner
			this.submitButton.disabled = true;
			this.submitButton.classList.add('gd-wait-btnsearch');

			// Store original button content
			this.originalButtonContent = this.submitButton.innerHTML;

			// Show spinner
			if (this.submitButton.tagName === 'BUTTON') {
				this.submitButton.innerHTML = '<i class="fas fa-hourglass fa-spin" aria-hidden="true"></i>';
			} else if (this.submitButton.tagName === 'INPUT') {
				this.submitButton.value = '...';
			}

			// Also disable filter buttons if they exist
			const showFilters = this.form.querySelector('.showFilters');
			if (showFilters) {
				showFilters.disabled = true;
			}
		} else {
			// Restore button
			this.submitButton.disabled = false;
			this.submitButton.classList.remove('gd-wait-btnsearch');

			// Restore original content
			if (this.originalButtonContent) {
				if (this.submitButton.tagName === 'BUTTON') {
					this.submitButton.innerHTML = this.originalButtonContent;
				} else if (this.submitButton.tagName === 'INPUT') {
					const originalText = this.submitButton.dataset.title || 'Search';
					this.submitButton.value = originalText;
				}
			}

			// Re-enable filter buttons
			const showFilters = this.form.querySelector('.showFilters');
			if (showFilters) {
				showFilters.disabled = false;
			}
		}
	}
}

/**
 * Initialize all search forms on the page
 */
export function initSearchForms() {
	const forms = document.querySelectorAll('form[name="geodir-listing-search"]');

	if (!forms.length) {
		return;
	}

	console.log(`GeoDir Search: Initializing ${forms.length} search form(s)`);

	forms.forEach(form => {
		new SearchForm(form);
	});
}

/**
 * Trigger search button click (helper for other scripts)
 * @param {HTMLElement} element - Element within search form
 */
export function triggerSearch(element) {
	setTimeout(() => {
		const searchForm = element.closest('.geodir-search');
		if (searchForm) {
			const submitButton = searchForm.querySelector('.geodir_submit_search');
			if (submitButton) {
				submitButton.click();
			}
		}
	}, 100);
}

// Expose helper globally for backward compatibility
window.geodirClickSearch = triggerSearch;
